import clientApi from 'api/clientApi'
import axiosInstance, { baseUrl } from 'api/axiosInstance'
import {
  CorrectionValues,
  DocumentDashboard,
  DocumentDetail,
  DocumentListParams,
  DocumentStats,
  DocumentSummary,
  HistoryEntry,
  PageResponse
} from './types'

const BASE = '/document-ai/v1/documents'

export type ClientInbox = {
  id: number
  emailId: string
  enable?: boolean
  objectType?: string
}

const DocumentsApiSlice = clientApi.injectEndpoints({
  endpoints: (build) => ({
    getDocuments: build.query<
      PageResponse<DocumentSummary>,
      DocumentListParams
    >({
      query: ({ clientId, queue, search, page, size, sort, direction }) => ({
        url: BASE,
        method: 'GET',
        params: {
          clientId,
          queue,
          search: search?.trim() || undefined,
          page,
          size,
          sort,
          direction
        }
      }),
      providesTags: ['clientApi']
    }),

    getDocumentStats: build.query<DocumentStats, { clientId: string }>({
      query: ({ clientId }) => ({
        url: `${BASE}/stats`,
        method: 'GET',
        params: { clientId }
      }),
      providesTags: ['clientApi']
    }),

    getDocumentDashboard: build.query<
      DocumentDashboard,
      { clientId?: string; days: number }
    >({
      query: ({ clientId, days }) => ({
        url: `${BASE}/dashboard`,
        method: 'GET',
        params: { clientId: clientId || undefined, days }
      }),
      providesTags: ['clientApi']
    }),

    getDocument: build.query<DocumentDetail, { id: string }>({
      query: ({ id }) => ({ url: `${BASE}/${id}`, method: 'GET' }),
      providesTags: ['clientApi']
    }),

    getDocumentHistory: build.query<HistoryEntry[], { id: string }>({
      query: ({ id }) => ({ url: `${BASE}/${id}/history`, method: 'GET' }),
      providesTags: ['clientApi']
    }),

    /** Original PDF as a Blob, for the preview and for downloads. */
    getDocumentFile: build.mutation<Blob, { id: number | string }>({
      query: ({ id }) => ({
        url: `${BASE}/${id}/file`,
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        responseType: 'blob'
      })
    }),

    getClientName: build.query<string | undefined, { clientId: string }>({
      query: ({ clientId }) => ({
        url: `/document-ai/v1/clients/${clientId}`,
        method: 'GET'
      }),
      transformResponse: (client: { name?: string }) => client?.name
    }),

    /** The client's inbound mailboxes, enabled ones first. */
    getClientInboxes: build.query<ClientInbox[], { clientId: string }>({
      query: ({ clientId }) => ({
        url: `/document-ai/v1/email-details/client/${clientId}`,
        method: 'GET'
      }),
      transformResponse: (inboxes: ClientInbox[]) =>
        [...(inboxes ?? [])].sort(
          (a, b) => Number(!!b.enable) - Number(!!a.enable)
        ),
      providesTags: ['clientApi']
    }),

    /** Sends corrected data through validation and delivery (202); poll the document. */
    correctDocument: build.mutation<
      DocumentSummary,
      { id: number | string; values: CorrectionValues; reviewedBy?: string }
    >({
      query: ({ id, values, reviewedBy }) => ({
        url: `${BASE}/${id}/extraction`,
        method: 'PUT',
        data: { ...values, reviewedBy }
      }),
      invalidatesTags: ['clientApi']
    }),

    /** Runs Document AI again in the background (202); poll the document. */
    reprocessDocument: build.mutation<DocumentSummary, { id: number | string }>(
      {
        query: ({ id }) => ({ url: `${BASE}/${id}/reprocess`, method: 'POST' }),
        invalidatesTags: ['clientApi']
      }
    )
  })
})

/**
 * Uploads one PDF with progress. Kept outside RTK Query because the shared
 * base query has no upload-progress hook.
 */
export const uploadDocument = (
  clientId: string,
  file: File,
  onProgress: (fraction: number) => void
) => {
  const data = new FormData()
  data.append('clientId', clientId)
  data.append('file', file)
  return axiosInstance.post<DocumentSummary>(`${baseUrl}${BASE}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (event.total) onProgress(event.loaded / event.total)
    }
  })
}

export const {
  useGetDocumentsQuery,
  useGetDocumentStatsQuery,
  useGetDocumentDashboardQuery,
  useGetDocumentQuery,
  useGetDocumentHistoryQuery,
  useGetDocumentFileMutation,
  useGetClientNameQuery,
  useGetClientInboxesQuery,
  useReprocessDocumentMutation,
  useCorrectDocumentMutation
} = DocumentsApiSlice

export const invalidateDocuments = () =>
  DocumentsApiSlice.util.invalidateTags(['clientApi'])
